/**
 * TopBar notification bell — reusable in-app inbox powered by
 * `/api/notifications/me`. Renders unread count, fires a friendly toast on
 * first mount for any unread items (deduped per session so a page navigation
 * doesn't re-toast), and a dropdown with recent notifications.
 *
 * All copy is server-driven — this component stays generic so any future
 * event (milestones, refunds, payouts, publishing failures) plugs straight
 * in without a code change.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Bell,
  Trophy,
  Crown,
  AlertTriangle,
  DollarSign,
  Sparkles,
  CheckCircle2,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Notification {
  notification_id: string;
  type: string;
  title: string;
  body: string;
  icon: string;
  cta_url: string | null;
  cta_label: string | null;
  read_at: string | null;
  created_at: string;
}

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Bell,
  Trophy,
  Crown,
  AlertTriangle,
  DollarSign,
  Sparkles,
  CheckCircle2,
};

// Show relative time ("2h", "3d") for compact chips.
function relTime(iso: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
    if (diff < 30 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return "";
  }
}

export const NotificationBell: React.FC = () => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const toastedIds = useRef<Set<string>>(new Set());

  const q = useQuery<{ items: Notification[]; unread_count: number }>({
    queryKey: ["notifications-me"],
    queryFn: async () => (await api.get("/notifications/me?limit=20")).data,
    // Poll softly so a new milestone/refund lands even without a reload.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const markAll = useMutation({
    mutationFn: async () => (await api.post("/notifications/mark-read", { ids: [] })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications-me"] }),
  });
  const markOne = useMutation({
    mutationFn: async (id: string) =>
      (await api.post(`/notifications/${id}/read`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications-me"] }),
  });

  // Fire toasts once per session for any unread notification we haven't seen.
  useEffect(() => {
    const unread = (q.data?.items || []).filter((n) => !n.read_at);
    for (const n of unread.slice(0, 3)) {
      if (toastedIds.current.has(n.notification_id)) continue;
      toastedIds.current.add(n.notification_id);
      const Icon = ICON_MAP[n.icon] || Bell;
      toast.custom(
        (t) => (
          <div
            className="pointer-events-auto flex items-start gap-3 bg-white dark:bg-slate-900 border border-[color:var(--sg-border)] shadow-lg rounded-xl p-3 max-w-sm"
            data-testid={`notification-toast-${n.notification_id}`}
          >
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-amber-100 to-fuchsia-100 dark:from-amber-950/50 dark:to-fuchsia-950/50 flex items-center justify-center shrink-0">
              <Icon className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-[color:var(--sg-fg)]">{n.title}</div>
              {n.body ? (
                <div className="text-[12px] text-[color:var(--sg-muted)] mt-0.5 line-clamp-2">{n.body}</div>
              ) : null}
              {n.cta_url ? (
                <button
                  onClick={() => {
                    markOne.mutate(n.notification_id);
                    toast.dismiss(t);
                    navigate(n.cta_url!);
                  }}
                  className="mt-1.5 text-[12px] font-semibold text-[color:var(--sb-saffron)] hover:underline"
                >
                  {n.cta_label || "View →"}
                </button>
              ) : null}
            </div>
          </div>
        ),
        { duration: 6000 },
      );
    }
  }, [q.data?.items, markOne, navigate]);

  const unread = q.data?.unread_count || 0;
  const items = q.data?.items || [];

  return (
    <DropdownMenu open={open} onOpenChange={(v) => {
      setOpen(v);
      if (v && unread > 0) markAll.mutate();
    }}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notification-bell"
          aria-label="Notifications"
          className="relative h-9 w-9 flex items-center justify-center rounded-full text-[color:var(--sg-fg)] hover:bg-[color:var(--sg-muted-bg)] transition-colors"
        >
          <Bell className="h-4 w-4" strokeWidth={2} />
          {unread > 0 ? (
            <span
              data-testid="notification-bell-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center leading-none tabular-nums"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-80 bg-[color:var(--sg-card)] p-0 overflow-hidden"
        data-testid="notification-dropdown"
      >
        <DropdownMenuLabel className="px-3 py-2.5 flex items-center justify-between border-b border-[color:var(--sg-border)]">
          <span className="text-sm font-semibold text-[color:var(--sg-fg)]">Notifications</span>
          {unread > 0 ? (
            <span className="text-[11px] text-[color:var(--sg-muted)]">{unread} new</span>
          ) : null}
        </DropdownMenuLabel>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-xs text-[color:var(--sg-muted)]">
              You're all caught up 🎉
            </div>
          ) : (
            items.map((n) => {
              const Icon = ICON_MAP[n.icon] || Bell;
              return (
                <button
                  key={n.notification_id}
                  onClick={() => {
                    markOne.mutate(n.notification_id);
                    setOpen(false);
                    if (n.cta_url) navigate(n.cta_url);
                  }}
                  data-testid={`notification-item-${n.notification_id}`}
                  className={`w-full text-left flex gap-2.5 px-3 py-2.5 border-b border-[color:var(--sg-border)] last:border-0 transition ${
                    n.read_at
                      ? "hover:bg-[color:var(--sg-muted-bg)]"
                      : "bg-amber-50/60 dark:bg-amber-950/20 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                  }`}
                >
                  <div className="h-8 w-8 shrink-0 rounded-lg bg-white dark:bg-slate-900 border border-[color:var(--sg-border)] flex items-center justify-center">
                    <Icon className="h-3.5 w-3.5 text-[color:var(--sg-fg)]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-[13px] font-semibold text-[color:var(--sg-fg)] truncate">{n.title}</div>
                      <div className="text-[10px] text-[color:var(--sg-muted)] shrink-0">{relTime(n.created_at)}</div>
                    </div>
                    {n.body ? (
                      <div className="text-[11.5px] text-[color:var(--sg-muted)] mt-0.5 line-clamp-2">{n.body}</div>
                    ) : null}
                    {n.cta_label && n.cta_url ? (
                      <div className="text-[11px] font-semibold text-[color:var(--sb-saffron)] mt-1">
                        {n.cta_label}
                      </div>
                    ) : null}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
